import { useEffect, useState } from 'react';
import { FiSearch, FiChevronDown, FiMenu } from 'react-icons/fi';
import { Link, useLocation } from 'react-router';
import { isCurrentNavLink, NAV_LINKS, MEGA_MENUS, SITE_LINKS, type MenuKey } from '@/data/navigation';
import MegaMenu from './MegaMenu';
import MobileMenu from './MobileMenu';
import LanguageSelector from './LanguageSelector';

export default function Navbar({ solid = false, intro = false }: { solid?: boolean; intro?: boolean }) {
    const [scrolled, setScrolled] = useState(false);
    const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
    const { pathname, hash } = useLocation();
    // A dropdown belongs to the page it was opened from. Once the route changes it
    // has nothing to do with what is on screen, so it goes — otherwise it sits over
    // the new page until the pointer happens to wander out of the header.
    useEffect(() => {
        setActiveMenu(null);
        setMobileOpen(false);
    }, [pathname, hash]);
    /**
     * Every top-level entry reads the same: dimmed until you point at it, full
     * white for the section you are in. Weight marks the current section only —
     * it never changes on hover, which would shift the whole bar sideways.
     */
    const navItemClass = (current: boolean, open = false): string =>
        `text-[15px] leading-6 transition-colors duration-200 ${
            current
                ? 'font-bold text-white'
                : open
                  ? 'font-medium text-white'
                  : 'font-medium text-white/70 hover:text-white'
        }`;
    const [mobileOpen, setMobileOpen] = useState(false);
    // With `intro`, the bar stays tucked above the viewport and slides down only
    // after the hero background finishes filling the screen (~2.3s after handoff).
    const [shown, setShown] = useState(!intro);

    useEffect(() => {
        const scroller = document.querySelector<HTMLElement>('.page-scroll-root');
        const target: HTMLElement | Window = scroller ?? window;

        // The bar stays translucent over the hero and turns solid only once the
        // NEXT section scrolls up under it. Threshold = the hero's rendered height
        // (first visible child of <main>) minus the nav height — measured live so
        // it's correct even before the hero has finished laying out.
        const navH = window.innerWidth >= 1024 ? 78 : 68;
        const heroHeight = () => {
            const main = document.querySelector('main');
            if (main) {
                for (const child of Array.from(main.children)) {
                    const h = (child as HTMLElement).offsetHeight;
                    if (h > 0) return h; // first visible child = the hero
                }
            }
            return window.innerHeight;
        };

        const read = () => {
            const top = scroller ? scroller.scrollTop : window.scrollY;
            setScrolled(top > Math.max(heroHeight() - navH, 0));
        };
        read();
        target.addEventListener('scroll', read, { passive: true });
        window.addEventListener('resize', read);
        return () => {
            target.removeEventListener('scroll', read);
            window.removeEventListener('resize', read);
        };
    }, []);

    useEffect(() => {
        if (!intro) return;
        // No preloader this visit (e.g. in-app navigation) → show immediately.
        if (!window.__cgPreloading) {
            setShown(true);
            return;
        }
        let timer: ReturnType<typeof setTimeout>;
        const onReveal = () => {
            timer = setTimeout(() => setShown(true), 2300);
        };
        window.addEventListener('cg:reveal-hero', onReveal);
        return () => {
            window.removeEventListener('cg:reveal-hero', onReveal);
            clearTimeout(timer);
        };
    }, [intro]);

    return (
        <>
            <header
                onMouseLeave={() => setActiveMenu(null)}
                className={`fixed inset-x-0 top-0 z-[2000] border-b transition-[translate,background-color,border-color] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    shown ? 'translate-y-0' : '-translate-y-full'
                } ${
                    solid || scrolled
                        ? 'border-white/5 bg-[#0a0a0a]/95 backdrop-blur-md'
                        : 'border-[rgba(204,204,204,0.1)] bg-black/10 backdrop-blur-md'
                }`}
            >
                <div className="flex h-[68px] items-center justify-between px-5 lg:h-[78px] lg:px-10 xl:px-[140px]">
                    {/* Logo */}
                    <Link to="/" className="shrink-0" onMouseEnter={() => setActiveMenu(null)}>
                        <img
                            src="/landing/icons/logo.svg"
                            alt="Citizens' Gavel"
                            className="h-7 w-[98px] lg:h-8 lg:w-[112px]"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden items-center gap-8 lg:flex xl:gap-12 2xl:gap-14">
                        {NAV_LINKS.map((link) => {
                            const current = isCurrentNavLink(link, pathname);
                            if (!link.menu) {
                                return (
                                    <Link
                                        key={link.label}
                                        to={link.href ?? '/'}
                                        onMouseEnter={() => setActiveMenu(null)}
                                        aria-current={current ? 'page' : undefined}
                                        className={navItemClass(current)}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            }
                            const isOpen = activeMenu === link.menu;
                            return (
                                <button
                                    key={link.label}
                                    type="button"
                                    onMouseEnter={() => setActiveMenu(link.menu!)}
                                    onClick={() => setActiveMenu(isOpen ? null : link.menu!)}
                                    aria-expanded={isOpen}
                                    aria-current={current ? 'page' : undefined}
                                    className={`flex items-center gap-1 ${navItemClass(current, isOpen)}`}
                                >
                                    {link.label}
                                    {link.chevron && (
                                        <FiChevronDown
                                            className={`h-5 w-5 text-[#ff6600] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </nav>

                    {/* Right cluster */}
                    <div className="flex items-center gap-4 lg:gap-6" onMouseEnter={() => setActiveMenu(null)}>
                        <button type="button" aria-label="Search" className="block text-white">
                            <FiSearch className="h-5 w-5" />
                        </button>
                        <LanguageSelector />
                        {SITE_LINKS.donate && (
                            <a
                                href={SITE_LINKS.donate}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden items-center justify-center rounded-[10px] bg-primary px-6 py-2.5 text-[16px] font-bold text-white transition-colors hover:bg-primary-600 lg:inline-flex"
                            >
                                Donate
                            </a>
                        )}
                        <button
                            type="button"
                            aria-label="Open menu"
                            onClick={() => setMobileOpen(true)}
                            className="text-white lg:hidden"
                        >
                            <FiMenu className="h-7 w-7" />
                        </button>
                    </div>
                </div>

                {/* Mega-menu dropdown. The outer band is click-through (pointer-events-none)
                    so hovering the empty space beside the panel doesn't keep the menu open —
                    only the bar and the panel itself do. Leaving both hides it immediately. */}
                {activeMenu && (
                    <div className="pointer-events-none absolute inset-x-0 top-full hidden lg:block">
                        <div className="flex justify-center px-5 lg:px-10 xl:px-[140px]">
                            <div className="pointer-events-auto">
                                <MegaMenu data={MEGA_MENUS[activeMenu]} onNavigate={() => setActiveMenu(null)} />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
        </>
    );
}
